
import { ShieldCheck, Bell, Book, GitMerge, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const ComplianceContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Contract and Regulatory Compliance</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered compliance tools ensure adherence to legal standards while managing risks effectively.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered compliance tools for legal teams offer advanced features to ensure adherence to legal standards, contractual and organizational policies while managing risks effectively. Benefits include faster compliance checks, reduced legal risks, improved accuracy in contract management, enhanced productivity through automation, and better regulatory oversight. These tools empower legal teams to focus on strategic initiatives while maintaining confidence in their compliance processes.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <ShieldCheck size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Risk Detection</h3>
            <p className="text-white/70">
              AI scans contracts for non-compliant clauses, regulatory risks, and inconsistencies, ensuring alignment with evolving laws and internal standards.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <Bell size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Regulatory Alerts</h3>
            <p className="text-white/70">
              Tools provide real-time updates on changes in laws or regulations relevant to the organization's jurisdiction or industry.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <Book size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Customizable Playbooks</h3>
            <p className="text-white/70">
              Preloaded templates and guidelines enable standardized contract reviews and revisions, ensuring consistency across agreements.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <GitMerge size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              Automates tasks like contract tracking, deadline monitoring, and approval routing, reducing manual effort and delays.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <Lock size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Data Security</h3>
            <p className="text-white/70">
              Advanced security measures protect sensitive contract data, ensuring compliance with privacy regulations like GDPR and CCPA.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Revolutionary Approach</h3>
            <p className="text-white/70 mb-4">
              Harnessing AI for regulatory compliance: A revolutionary approach
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.ishn.com/articles/114267-harnessing-ai-for-regulatory-compliance-a-revolutionary-approach', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Dodd-Frank Compliance</h3>
            <p className="text-white/70 mb-4">
              How Goldman Sachs uses Eigen to manage Dodd-Frank compliance across their entire portfolio of Qualified Financial Contracts (QFCs)
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.sirion.ai/library/case-study/eigen/automating-document-processing-for-regulatory-reporting-and-compliance/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Lending Compliance</h3>
            <p className="text-white/70 mb-4">
              Navigating Compliance with AI - Doe Gregersen's Experience at Above Lending
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.paxton.ai/post/navigating-compliance-with-ai-doe-gregersens-experience-at-above-lending', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Contract Output</h3>
            <p className="text-white/70 mb-4">
              Raiffeisen Bank International Increases Contract Output by 60%, Improving Regulatory Compliance
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.sirion.ai/library/case-study/raiffeisen-bank-international/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Supplier Compliance</h3>
            <p className="text-white/70 mb-4">
              Oaktree Improves Compliance For 300+ Suppliers With Contract Data Extraction
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.sirion.ai/library/case-study/oaktree-capital-management/', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Strengthen Your Compliance Strategy</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered compliance solutions can help your organization stay ahead of regulatory changes.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/advisory">
            <Button variant="outline" size="lg">Explore Advisory Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComplianceContent;
